import {ChangeDetectorRef, Component, ViewChild} from "@angular/core";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {DialogComponent} from "../bootstrap-modal/dialog.component";
import {UploadItem} from "./upload-item";
import {FileItem, FileUploader, ParsedResponseHeaders} from "ng2-file-upload";
import {UrlParams} from "../http/url-params-serializer";
import {
  DefaultUploadResponseProcessor,
  FileUploadDisplayUpdateInfo,
  ResponseProcessor,
  UploaderDialogModel,
  UploaderDialogResult,
} from "./uploader-dialog.model";

@Component({
  templateUrl: "./uploader-dialog.template.pug",
})
export class UploaderDialogComponent
  extends DialogComponent<UploaderDialogModel, UploaderDialogResult>
  implements UploaderDialogModel {
  item: UploadItem;
  isFileOver: boolean;
  uploader: FileUploader;
  responseProcessor: ResponseProcessor = DefaultUploadResponseProcessor;
  fileExtensionsStr: string = "";
  title: string;

  hideCloseButton: boolean;
  hideAnotherFileButton: boolean;
  hideUploadButton: boolean;
  hideRemoveButton: boolean;
  @ViewChild("selectedFile") private selectedFile: any;

  set fileExtensions(v: string[]) {
    this.uploader.options.allowedFileType = v as string[];
    this.uploader.setOptions(this.uploader.options);

    if (!v || v.length <= 0) {
      this.fileExtensionsStr = "";
    } else if (v.length === 1) {
      this.fileExtensionsStr = `"${v[0]}"`;
    } else if (v.length > 1) {
      const copy = v.map(f => `"${f}"`);
      const last = copy.pop();
      this.fileExtensionsStr = `${copy.join(", ")}, or ${last}`;
    }
  }

  get fileExtensions(): string[] {
    return this.uploader.options.allowedFileType as string[];
  }

  set url(url: string) {
    this.uploader.options.url = url;
  }

  get url(): string {
    return this.uploader.options.url;
  }

  set params(params: UrlParams) {
    this.uploader.options.additionalParameter = params;
  }

  get params(): UrlParams {
    return this.uploader.options.additionalParameter as UrlParams;
  }

  fileOver($event) {
    this.isFileOver = Boolean($event);
  }

  getXsrfToken() {
    return document.cookie.split(/;\s*/)
      .map((str) => {
        let [, key, value] = str.match(/^([^=]+)=(.*)$/) || [null, null, null];

        if (!key) return undefined;
        if (key !== "XSRF-TOKEN") return undefined;

        if (value[0] === "\"") {
          value = value.slice(1, -1);
        }

        value = decodeURIComponent(value);

        return value;
      })
      .filter(it => !!it)[0] || "";
  }

  uploadItem() {
    if (!this.item || !this.item.fileItem) return;

    this.hideAnotherFileButton = true;
    this.hideRemoveButton = true;
    this.hideUploadButton = true;
    this.hideCloseButton = true;

    if (!this.uploader.options.headers) this.uploader.options.headers = [];

    const xsrfHeaderIndex = this.uploader.options.headers.findIndex(it => it.name === "X-Xsrf-Token");
    if (xsrfHeaderIndex >= 0) {
      this.uploader.options.headers[xsrfHeaderIndex].value = this.getXsrfToken();
    } else {
      this.uploader.options.headers.push({name: "X-Xsrf-Token", value: this.getXsrfToken()});
    }

    this.uploader.setOptions(this.uploader.options);

    this.item.fileItem.upload();
  }

  removeItem() {
    if (!this.item || !this.item.fileItem) return;

    this.hideAnotherFileButton = true;
    this.hideRemoveButton = true;
    this.hideUploadButton = true;
    this.hideCloseButton = false;

    this.item.fileItem.remove();

    if (this.uploader.queue.length) {
      this.uploader.clearQueue();
    }
    this.selectedFile.nativeElement.value = "";

    this.item = undefined;
  }

  private updateDisplayItem(fileUploadDisplayUpdateInfo: FileUploadDisplayUpdateInfo) {
    const {complete, uploading, success, response, status, headers, progress} = fileUploadDisplayUpdateInfo;

    const displayItem = this.item;
    if (complete !== undefined) displayItem.complete = complete;
    if (uploading !== undefined) displayItem.uploading = uploading;
    if (status !== undefined) displayItem.status = status;
    if (headers !== undefined) displayItem.headers = headers;
    if (success !== undefined) displayItem.success = success;
    if (progress !== undefined) displayItem.progress = +progress || 0;
    if (response !== undefined) {
      this.responseProcessor(response)
        .then(
          (result) => {
            displayItem.response = result && typeof result.toString === "function" ? result.toString() : `${result}`;

            if (success === undefined) return;
            this.result.responses.push(result);
            this.result.total += 1;
          },
          (error) => {
            displayItem.error = error;

            if (success === undefined) return;
            this.result.errors.push(error as Error);
            this.result.total += 1;
          },
        )
        .then(() => this.changeDetectorRef.detectChanges());
    } else {
      this.changeDetectorRef.detectChanges();
    }
  }

  private addUploaderMethods() {
    this.uploader.onAfterAddingFile = (fileItem: FileItem): any => {
      this.item = {
        fileItem,
        name: fileItem.file.name || "File",
        size: (+fileItem.file.size || 0) / 1024,
        progress: 0,
        complete: false,
        uploading: false,
        success: undefined,
      } as UploadItem;

      this.hideAnotherFileButton = true;
      this.hideRemoveButton = false;
      this.hideUploadButton = false;
      this.hideCloseButton = false;

      return {fileItem};
    };

    this.uploader.onSuccessItem = (
      item: FileItem,
      response: string,
      status: number,
      headers: ParsedResponseHeaders,
    ): any => {
      this.updateDisplayItem({complete: true, uploading: false, success: true, response, status, headers});

      return {item, response, status, headers};
    };

    this.uploader.onErrorItem = (
      item: FileItem,
      response: string,
      status: number,
      headers: ParsedResponseHeaders,
    ): any => {
      this.updateDisplayItem({complete: true, uploading: false, success: false, response, status, headers});

      return {item, response, status, headers};
    };

    this.uploader.onCompleteItem = (
      item: FileItem,
      response: string,
      status: number,
      headers: ParsedResponseHeaders,
    ): any => {
      this.updateDisplayItem({complete: true, uploading: false, response, status, headers});

      this.hideAnotherFileButton = false;
      this.hideRemoveButton = true;
      this.hideUploadButton = true;
      this.hideCloseButton = false;

      return {item, response, status, headers};
    };

    this.uploader.onBeforeUploadItem = (fileItem: FileItem): any => {
      this.updateDisplayItem({complete: false, uploading: true});

      this.hideAnotherFileButton = true;
      this.hideRemoveButton = true;
      this.hideUploadButton = true;
      this.hideCloseButton = true;

      return {fileItem};
    };

    this.uploader.onProgressItem = (fileItem: FileItem, progress: any): any => {
      this.updateDisplayItem({complete: false, uploading: true, progress});

      return {fileItem, progress};
    };

    this.uploader.onCancelItem = (
      item: FileItem,
      response: string,
      status: number,
      headers: ParsedResponseHeaders,
    ): any => {
      this.updateDisplayItem({complete: true, uploading: false, response, status, headers});

      this.hideAnotherFileButton = false;
      this.hideRemoveButton = true;
      this.hideUploadButton = true;
      this.hideCloseButton = false;

      return {item, response, status, headers};
    };
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    public dialogService: DialogService,
  ) {
    super(dialogService);
    this.result = {responses: [], errors: [], total: 0};

    this.uploader = new FileUploader({url: "", queueLimit: 1});
    this.addUploaderMethods();

    this.hideAnotherFileButton = true;
    this.hideRemoveButton = true;
    this.hideUploadButton = true;
    this.hideCloseButton = false;
  }
}
